import type { StoragePort } from './storagePort.ts';

/**
 * In-memory storage. Used when the app runs in a plain browser (npm run dev)
 * and by tests. Data is lost when the page closes, which the UI warns about.
 */
export function createMemoryStorage(): StoragePort {
  const characters = new Map<string, string>();
  const sessions = new Map<string, string>();
  const journals = new Map<string, string>();
  const runtimes = new Map<string, string>();
  let index: string | null = null;
  const customContent = new Map<string, string>();

  return {
    async listCharacterIds() {
      return [...characters.keys()];
    },
    async readCharacter(id) {
      const raw = characters.get(id);
      return raw === undefined ? null : JSON.parse(raw);
    },
    async writeCharacter(id, data) {
      characters.set(id, JSON.stringify(data));
    },
    async deleteCharacter(id) {
      characters.delete(id);
    },
    async readSession(characterId) {
      const raw = sessions.get(characterId);
      return raw === undefined ? null : JSON.parse(raw);
    },
    async writeSession(characterId, data) {
      sessions.set(characterId, JSON.stringify(data));
    },
    async deleteSession(characterId) {
      sessions.delete(characterId);
    },
    async readJournal(characterId) {
      const raw = journals.get(characterId);
      return raw === undefined ? null : JSON.parse(raw);
    },
    async writeJournal(characterId, data) {
      journals.set(characterId, JSON.stringify(data));
    },
    async deleteJournal(characterId) {
      journals.delete(characterId);
    },
    async readRuntime(characterId) {
      const raw = runtimes.get(characterId);
      return raw === undefined ? null : JSON.parse(raw);
    },
    async writeRuntime(characterId, data) {
      runtimes.set(characterId, JSON.stringify(data));
    },
    async deleteRuntime(characterId) {
      runtimes.delete(characterId);
    },
    async readCustomContent(name) {
      const raw = customContent.get(name);
      return raw === undefined ? null : JSON.parse(raw);
    },
    async writeCustomContent(name, data) {
      customContent.set(name, JSON.stringify(data));
    },
    async readIndex() {
      return index === null ? null : JSON.parse(index);
    },
    async writeIndex(data) {
      index = JSON.stringify(data);
    },
    async describeLocation() {
      return 'Temporary memory (nothing is being saved to disk)';
    },
  };
}
